/**
 * ParentsTable Component
 *
 * Таблица родителей на основе DataTable.
 * Колонки: Email, ФИО, Телефон, Дети, Центры, Статус.
 */
import React from 'react';
import { Text, Badge, Stack } from '@mantine/core';
import DataTable from '../common/DataTable';
import tokens from '../../theme/designTokens';

const getColumns = () => [
  {
    key: 'email',
    label: 'Email',
    sortable: true,
    filterable: false,
    searchable: true,
    renderCell: (value) => (
      <Text size="sm" c={tokens.colors.neutral[8]}>{value}</Text>
    ),
  },
  {
    key: 'full_name',
    label: 'ФИО',
    sortable: true,
    filterable: false,
    searchable: true,
    searchValue: (value, row) =>
      [row.last_name, row.first_name].filter(Boolean).join(' '),
    renderCell: (value, row) => {
      const name = [row.last_name, row.first_name].filter(Boolean).join(' ');
      if (!name) return <Text size="sm" c="dimmed">—</Text>;
      return <Text size="sm" fw={600} c={tokens.colors.neutral[9]}>{name}</Text>;
    },
  },
  {
    key: 'phone',
    label: 'Телефон',
    sortable: false,
    filterable: false,
    renderCell: (value) => {
      if (!value) return <Text size="sm" c="dimmed">—</Text>;
      return <Text size="sm">{value}</Text>;
    },
  },
  {
    key: 'students',
    label: 'Дети',
    sortable: false,
    filterable: false,
    renderCell: (value) => {
      const students = value || [];
      if (students.length === 0) return <Text size="sm" c="dimmed">—</Text>;
      return (
        <Stack gap={2}>
          {students.map((s) => (
            <Badge key={s.id} size="xs" variant="filled" color="indigo" radius="xl">
              {s.full_name || s.first_name}
            </Badge>
          ))}
        </Stack>
      );
    },
  },
  {
    key: 'organizations',
    label: 'Центры',
    sortable: false,
    filterable: false,
    renderCell: (value) => {
      const orgs = value || [];
      if (orgs.length === 0) return <Text size="sm" c="dimmed">—</Text>;
      return (
        <Stack gap={2}>
          {orgs.map((o) => (
            <Badge key={o.id} size="xs" variant="filled" color="blue" radius="xl">
              {o.name}
            </Badge>
          ))}
        </Stack>
      );
    },
  },
  {
    key: 'is_active',
    label: 'Статус',
    sortable: false,
    filterable: true,
    filterOptions: [
      { value: 'true', label: 'Активен' },
      { value: 'false', label: 'Неактивен' },
    ],
    filterDisplayValue: (value) => (value ? 'Активен' : 'Неактивен'),
    renderCell: (value) => (
      <Badge size="xs" variant="filled" color={value ? 'green' : 'gray'} radius="xl">
        {value ? 'Активен' : 'Неактивен'}
      </Badge>
    ),
  },
];

const ParentsTable = ({
  data, filters, onFiltersChange, searchValue, onSearchChange,
  onSortChange, onRowClick,
}) => {
  const columns = getColumns();

  const handleRenderCell = (row, column) => {
    const colDef = columns.find((c) => c.key === column.key);
    if (colDef?.renderCell) return colDef.renderCell(row[column.key], row);
    return undefined;
  };

  return (
    <DataTable
      columns={columns}
      data={data}
      filters={filters}
      onFiltersChange={onFiltersChange}
      skipProcessing={!!onSearchChange}
      onSearchChange={onSearchChange}
      onSortChange={onSortChange}
      searchValue={searchValue}
      sortable={true}
      onRowClick={onRowClick}
      renderCell={handleRenderCell}
    />
  );
};

export default ParentsTable;
